"use client";
import React, { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import { Box, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  clearContactEmailConfirmationRequest,
  markContactEmailConfirmationCompleted,
  requestProfileReviewModal,
  updateProfile,
} from "@/store/apps/userData/UserDataSlice";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";

export default function EmailNotifier({
  hasOtherNotifications = false,
  onActiveChange = () => {},
}) {
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [sentTo, setSentTo] = useState("");
  // magic_email_pending_choice: tracks which email the user selects when they
  // signed in with a different email than their LinkedIn email.
  const [chosenEmail, setChosenEmail] = useState("linkedin");
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // While the hub URL carries `?id=...` the user is mid-booking (the
  // verify-card / verify-mobile / payment modals run on local state,
  // not Redux flags, so isProcessingBooking/showSentModal can be false
  // during that step). Defer until the booking flow cleans up the URL.
  const hasBookingIdInUrl = !!searchParams?.get("id");
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);
  const contactEmailConfirmationRequested = useSelector(
    (state) => state.user.contactEmailConfirmationRequested,
  );
  // Booking-flow signals — suppress the welcome dialog while the seeker
  // is in the middle of a booking flow. The booking success modal
  // (SentModal) handles email confirmation in that context.
  const isProcessingBooking = useSelector(
    (state) => state.user.isProcessingBooking,
  );
  const showSentModal = useSelector((state) => state.user.showSentModal);
  const isInstantBookingFlow = useSelector(
    (state) => state.user.isInstantBookingFlow,
  );
  // Suppress during the enrichment loading phase. EnrichmentNotifier reports
  // back via the hasOtherNotifications prop, but that update happens one React
  // render cycle after fetchUserData sets email_acknowledged = false — causing
  // a brief flash of this modal before the loading overlay appears. Reading
  // needsEnrichment / isInitializationComplete directly from Redux lets us
  // suppress eagerly in the same render that profile data first arrives.
  const needsEnrichment = useSelector((state) => state.user.needsEnrichment);
  const isInitializationComplete = useSelector(
    (state) => state.user.isInitializationComplete,
  );

  // Existing users (email_acknowledged === undefined) are never affected.
  const needsAcknowledgment = profile?.email_acknowledged === false;
  // True when the user signed in with a different email than their LinkedIn
  // email and needs to choose which one to use for communications.
  const hasMagicChoice =
    !!profile?.magic_email_pending_choice && !!profile?.email;
  // The welcome modal is only for users with no booking-related ack
  // pending and no in-flight email replacement — both of those cases
  // are owned by PendingEmailNotifier (the persistent banner). Showing
  // the modal on top of the banner would either double-prompt or show
  // stale copy ("Is `<old>` correct?" while a replacement link is out).
  const ownedByBanner =
    profile?.email_pending_replacement === true ||
    !!profile?.pending_booking_request_id;

  const suppressed =
    !needsAcknowledgment ||
    !profile?.email ||
    (!contactEmailConfirmationRequested &&
      (pathname?.startsWith("/settings") ||
        // Public booking surface — verify-card / verify-mobile / schedule
        // modals live here for paid meetings, and the booking flow ends with
        // SentModal which owns email confirmation in that context.
        pathname?.startsWith("/profile") ||
        hasBookingIdInUrl ||
        isProcessingBooking ||
        isInstantBookingFlow ||
        showSentModal ||
        ownedByBanner ||
        // Eagerly suppress while enrichment is still needed — prevents a
        // one-render flash before EnrichmentNotifier's onActiveChange fires.
        // needsEnrichment starts as null (initial Redux state) and profile
        // arrives in a separate dispatch before needsEnrichment is set, so we
        // must suppress on null too, not just on true.
        (needsEnrichment !== false && isInitializationComplete !== true) ||
        // Defer until enrichment / vanity-modal flow finishes — showing both
        // backdrops at the same time stacks the dark overlays and looks broken.
        hasOtherNotifications));

  // Surface our presence to DashboardLayout for downstream-notifier
  // suppression. The "sent" success dialog is transient so we don't
  // count it — only the welcome / change dialogs hold the slot.
  useEffect(() => {
    onActiveChange(!suppressed);
    return () => onActiveChange(false);
  }, [suppressed, onActiveChange]);

  const acknowledge = async (useMagicEmail = false) => {
    setIsSubmitting(true);
    setErrorMsg("");
    const returnToProfileReview = contactEmailConfirmationRequested;
    try {
      const res = await fetch("/api/user/acknowledge-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ useMagicEmail }),
      });
      const data = await res.json();

      if (!res.ok || !data?.success) {
        // Hunter rejected — acknowledge still flipped server-side so the
        // dialog won't loop, but we surface the error so the user knows
        // to use a different address via "Update email".
        setErrorMsg(
          data?.message ||
            "We couldn't verify this email. Please update it to a valid address.",
        );
        // Still mark acknowledged in Redux so the dialog closes if they
        // choose to dismiss — they can fix it via "Update email" flow.
        dispatch(
          updateProfile({ email_acknowledged: true, email_verified: false }),
        );
        dispatch(clearContactEmailConfirmationRequest());
        dispatch(markContactEmailConfirmationCompleted());
        if (returnToProfileReview) {
          dispatch(requestProfileReviewModal({ emailConfirmed: true }));
        }
        return;
      }

      dispatch(
        updateProfile({
          email_acknowledged: true,
          email_verified: data.verified ?? true,
        }),
      );
      dispatch(clearContactEmailConfirmationRequest());
      dispatch(markContactEmailConfirmationCompleted());
      if (returnToProfileReview) {
        dispatch(requestProfileReviewModal({ emailConfirmed: true }));
      }
    } catch (error) {
      console.error("Failed to acknowledge email:", error);
      setErrorMsg("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    await acknowledge(hasMagicChoice && chosenEmail === "magic");
  };

  // Inline change-email — mirrors the settings flow (POST
  // /api/email/verify-email) so direct-signup users don't have to
  // navigate away to update their address. The endpoint runs Hunter
  // and, on pass, queues a verification link to the new address while
  // marking `email_acknowledged: true` server-side so this dialog
  // doesn't reappear with the stale original.
  const handleSendChange = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setIsSubmitting(true);
    setErrorMsg("");
    const returnToProfileReview = contactEmailConfirmationRequested;
    try {
      const res = await fetch("/api/email/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newEmail: trimmed }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setErrorMsg(data?.message || "Couldn't send link. Please try again.");
        return;
      }
      dispatch(
        updateProfile({
          email_acknowledged: true,
          pending_email: trimmed,
          email_token_expiry: Date.now() + 86400000,
        }),
      );
      dispatch(clearContactEmailConfirmationRequest());
      dispatch(markContactEmailConfirmationCompleted());
      if (returnToProfileReview) {
        dispatch(requestProfileReviewModal({ emailConfirmed: true }));
      } else {
        setSentTo(trimmed);
      }
      setChangeOpen(false);
      setNewEmail("");
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render the "Check your inbox" success dialog even after the
  // welcome dialog is suppressed (acknowledged flips true on success).
  if (suppressed && !sentTo) return null;

  return (
    <>
      {/* Backdrop — makes the blocking gate visually obvious */}
      {!suppressed && !changeOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.15)",
            backdropFilter: "blur(4px)",
            zIndex: 1299,
            pointerEvents: "none",
          }}
        />
      )}

      {!suppressed && (
        <ConfirmDialog
          open={!changeOpen}
          onClose={() => {}}
          onConfirm={handleConfirm}
          onIgnore={() => {
            setErrorMsg("");
            setChangeOpen(true);
          }}
          title="Confirm contact email"
          body={
            hasMagicChoice ? (
              // Two-choice version: user signed in with a different email than
              // their LinkedIn email. Let them pick which to use for comms.
              <>
                We&apos;ll send meeting requests and notifications to your
                email. Which address should we use?
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  {[
                    {
                      key: "linkedin",
                      label: profile.email,
                      caption: "From LinkedIn",
                    },
                    {
                      key: "magic",
                      label: profile.magic_email_pending_choice,
                      caption: "Signed in with this",
                    },
                  ].map((opt) => (
                    <Box
                      key={opt.key}
                      onClick={() => setChosenEmail(opt.key)}
                      sx={{
                        p: 1.5,
                        border: "1.5px solid",
                        borderColor:
                          chosenEmail === opt.key ? "primary.main" : "divider",
                        borderRadius: "10px",
                        cursor: "pointer",
                        transition: "border-color 0.15s",
                      }}
                    >
                      <Box
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          wordBreak: "break-all",
                        }}
                      >
                        {opt.label}
                      </Box>
                      <Box
                        sx={{ fontSize: "0.78rem", color: "text.secondary" }}
                      >
                        {opt.caption}
                      </Box>
                    </Box>
                  ))}
                </Box>
                {errorMsg && (
                  <Box
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: "0.85rem",
                      mt: 1.5,
                    }}
                  >
                    {errorMsg}
                  </Box>
                )}
              </>
            ) : (
              // Standard version: confirm the LinkedIn email
              <>
                We&apos;ll send meeting requests and notifications to your
                email. Is the address below correct?
                <br />
                <strong
                  style={{
                    display: "block",
                    marginTop: "10px",
                    color: "var(--mui-palette-primary-main)",
                    textDecoration: "underline",
                  }}
                >
                  {profile.email}
                </strong>
                {errorMsg && (
                  <Box
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: "0.85rem",
                      mt: 1.5,
                    }}
                  >
                    {errorMsg}
                  </Box>
                )}
              </>
            )
          }
          confirmButtonText="Confirm"
          minHeight={260}
          mobileMinHeight={320}
          confirmButtonVariant="text"
          confirmButtonSx={{ px: 3 }}
          ignoreButtonSx={{ px: 3 }}
          ignoreButtonText="Change email"
          showIgnoreButton={true}
          showConfirmButton={true}
          showCloseIcon={false}
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
          isProcessing={isSubmitting}
          processingText="Updating"
        />
      )}

      <ConfirmDialog
        open={changeOpen}
        onClose={() => {
          if (isSubmitting) return;
          setChangeOpen(false);
          setErrorMsg("");
          setNewEmail("");
        }}
        onConfirm={handleSendChange}
        onIgnore={() => {
          if (isSubmitting) return;
          setChangeOpen(false);
          setErrorMsg("");
          setNewEmail("");
        }}
        title="Use a different email"
        confirmButtonText="Send link"
        ignoreButtonText="Cancel"
        showIgnoreButton={true}
        showConfirmButton={true}
        showCloseIcon={true}
        isProcessing={isSubmitting}
        processingText="Sending..."
        disableConfirm={!newEmail.trim()}
        body={
          <>
            Enter the email where you want to receive meeting updates and
            communication. We&apos;ll send a verification link to confirm it.
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="you@example.com"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  if (errorMsg) setErrorMsg("");
                }}
                disabled={isSubmitting}
                autoFocus
              />
              {errorMsg && (
                <Box
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: "0.85rem",
                    mt: 1,
                  }}
                >
                  {errorMsg}
                </Box>
              )}
            </Box>
          </>
        }
      />

      <ConfirmDialog
        open={!!sentTo}
        onClose={() => setSentTo("")}
        onConfirm={() => setSentTo("")}
        title="Check your inbox"
        confirmButtonText="Got it"
        showIgnoreButton={false}
        showConfirmButton={true}
        showCloseIcon={true}
        body={
          <>
            We sent a verification link to <strong>{sentTo}</strong>. Click it
            to switch your contact email. Until then your original address stays
            on file.
          </>
        }
      />
    </>
  );
}
